import { useEffect, useCallback, useRef, useState } from 'react';
import { HttpMethods } from 'shared/enums/HttpMethods';
import { useAuth } from 'shared/providers/AuthProvider';
import { ClinicUserFragment } from 'shared/types/graphql';
import http from 'shared/utils/http';
import { env } from 'env';
import { ContentTypes } from 'shared/enums/ContentTypes';
import { ZendeskTicket } from 'shared/types/zendesk';

declare global {
  interface Window {
    zE: Function; // eslint-disable-line @typescript-eslint/ban-types
    zESettings: Record<string, any>; // eslint-disable-line @typescript-eslint/no-explicit-any
  }
}

interface FormBuildProps {
  clinicId: string;
  clinicName: string;
  userName: string;
  userEmail: string;
  files: Array<File>;
  sfAccountId: string;
  links: string[];
  skipZendeskTicket?: boolean;
  additionalNote?: string;
}

interface ZendeskTicketResponse extends Response {
  result: string;
  error?: string;
  successfulUploads?: Record<string, string>;
  failedUploads?: Record<string, string>;
}

export const ZENDESK_ARTICLE_BASE_URL = 'https://help.otto.vet/hc/en-us/articles';
export const ZENDESK_TICKET_BASE_URL = 'https://ottovet.zendesk.com/agent/tickets';

const useZendesk = (
  user: ClinicUserFragment | null,
): {
  isZendeskOpen: boolean;
  createFormBuildTicket: (props: FormBuildProps) => Promise<ZendeskTicketResponse | undefined>;
  createTicket: (input: ZendeskTicket) => Promise<ZendeskTicketResponse | undefined>;
  createTicketComment: (ticketId: string, input: ZendeskTicket) => Promise<ZendeskTicketResponse | undefined>;
} => {
  const { isAuthenticated, isImpersonating, getToken } = useAuth();

  const zendeskToken = useRef<string | null>(null);
  const [isZendeskOpen, setIsZendeskOpen] = useState(false);

  const getZendeskToken = useCallback(async () => {
    if (!user?.email || !user.id) {
      return;
    }
    const { id, email, firstName, lastName } = user;
    const token = await getToken();
    if (!token) return;

    const response = await http<{ data?: string | null } | undefined>({
      method: HttpMethods.Post,
      url: `${env.REACT_APP_CORE_API_URI}/zendesk/jwt`,
      token,
      body: JSON.stringify({
        email,
        external_id: id,
        iat: Math.floor(Date.now() / 1000),
        name: [firstName, lastName].join(' ').trim(),
        // While we figure out a way to go back to use our own
        // ids for the user, we are disabling the scope for now
        // scope: 'user',
      }),
    });
    zendeskToken.current = response?.data || null;
  }, [user, getToken]);

  const initializeZendeskWidget = useCallback(() => {
    getZendeskToken();
    if (!document.getElementById('ze-snippet')) {
      const script = document.createElement('script');
      script.id = 'ze-snippet';
      script.src = 'https://static.zdassets.com/ekr/snippet.js?key=42d259ed-56b9-4453-803b-7c8c81d99dd1';
      script.onload = async (): Promise<void> => {
        if (!zendeskToken.current) {
          await getZendeskToken();
        }

        if (zendeskToken.current && window.zE) {
          window.zE('messenger', 'loginUser', function (callback: (token: string) => void) {
            callback(zendeskToken.current || '');
          });
        }
        if (window.zE) {
          window.zE('messenger', 'close');
        }
        if (window.zE) {
          window.zE('messenger:on', 'open', function () {
            console.log('Opening Zendesk widget');
            setIsZendeskOpen(true);
          });
          window.zE('messenger:on', 'close', function () {
            console.log('Closing Zendesk widget');
            setIsZendeskOpen(false);
          });
        }
      };

      document.body.appendChild(script);
    }
  }, [getZendeskToken]);

  // Once the user is authenticated, initialize the widget
  useEffect(() => {
    if (isAuthenticated && user && !isImpersonating) {
      initializeZendeskWidget();
    }
  }, [user, isAuthenticated, isImpersonating, initializeZendeskWidget]);

  useEffect(() => {
    if (!window.zE) return;
    if (!isAuthenticated && !isImpersonating) {
      if (window.zE) {
        window.zE('messenger', 'logoutUser');
      }
    }
  }, [isAuthenticated, isImpersonating, user]);

  const createFormBuildTicket = async (props: FormBuildProps): Promise<ZendeskTicketResponse | undefined> => {
    const token = await getToken();
    if (!token) return;

    const formData = new FormData();
    formData.append('clinicId', props.clinicId);
    formData.append('clinicName', props.clinicName);
    formData.append('impersonatedName', props.userName);
    formData.append('impersonatedEmail', props.userEmail);
    props.files.forEach((file) => {
      formData.append(file.name, file);
    });
    formData.append('sfAccountId', props.sfAccountId);
    formData.append('links', JSON.stringify(props.links));
    formData.append('skipZendeskTicket', String(props.skipZendeskTicket || false));
    formData.append('additionalNote', props.additionalNote || '');

    return await http<ZendeskTicketResponse>({
      method: HttpMethods.Post,
      url: `${env.REACT_APP_CORE_API_URI}/zendesk/tickets/formBuild`,
      token,
      contentType: ContentTypes.MultiPartFormData,
      body: formData,
    });
  };

  const createTicket = async (input: ZendeskTicket): Promise<ZendeskTicketResponse | undefined> => {
    // generic zendesk ticket creation, used for all tickets except formBuild
    const token = await getToken();
    if (!token) return;

    return await http<ZendeskTicketResponse>({
      method: HttpMethods.Post,
      url: `${env.REACT_APP_CORE_API_URI}/zendesk/tickets/`,
      token,
      contentType: ContentTypes.Json,
      body: JSON.stringify(input),
    });
  };

  const createTicketComment = async (
    ticketId: string,
    input: ZendeskTicket,
  ): Promise<ZendeskTicketResponse | undefined> => {
    const token = await getToken();
    if (!token) return;

    return await http<ZendeskTicketResponse>({
      method: HttpMethods.Put,
      url: `${env.REACT_APP_CORE_API_URI}/zendesk/tickets/${ticketId}`,
      token,
      contentType: ContentTypes.Json,
      body: JSON.stringify(input),
    });
  };

  return {
    isZendeskOpen: isZendeskOpen,
    createFormBuildTicket,
    createTicket,
    createTicketComment,
  };
};

export default useZendesk;
